
import Vue, { PropType } from 'vue';
import { RunnitNodeField, RUNNIT_NODE_FIELD_TYPE, RunnitNodeRunInputValue, getLogicOnFieldsFromField } from '@run-diffusion/shared';
import { mapState, mapActions } from 'vuex';
import WidthHeightField from '@/views/Runnits/RunnitSettings/PromptDrivenForm/ChipFields/WidthHeightField/WidthHeightField.vue';
import SelectField from '@/views/Runnits/RunnitSettings/PromptDrivenForm/ChipFields/SelectField/SelectField.vue';
import SliderField from '@/views/Runnits/RunnitSettings/PromptDrivenForm/ChipFields/SliderField/SliderField.vue';
import { isChipSliderFieldType } from '@/views/Runnits/RunnitSettings/PromptDrivenForm/ChipFields/utils';
import _isNil from 'lodash/isNil';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { applyAspectRatioDisabledMessage } from '@/utils/aspectRatio';

export default Vue.extend({
	name: 'DynamicChipField',
	props: {
		field: {
			type: Object as PropType<RunnitNodeField>,
			required: true,
		},
		value: {
			type: [String, Number, Object],
			required: false,
		},
		inputValues: {
			type: Object as PropType<RunnitNodeRunInputValue>,
			required: true,
		},
	},
	data () {
		return {
			RUNNIT_NODE_FIELD_TYPE,
		};
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		isChipSliderFieldType,
		onFieldInput ({ field, value }: { field: RunnitNodeField, value: any }) {
			const isAutoSetValue = this.isValueEqualToAutoSet(value);

			// Allow autoSet values to pass through even if field is disabled
			// This lets the autoSet mechanism update the field while preventing manual edits
			if (this.isDisabled && !isAutoSetValue) {
				return;
			}

			this.$emit('input', { field, value });
		},
		isValueEqualToAutoSet (value: any): boolean {
			const autoSetValue = this.autoSetValue;
			if (_isNil(autoSetValue)) {
				return false;
			}

			// Direct equality for primitives
			if (value === autoSetValue) {
				return true;
			}

			// For string/number values
			if (typeof autoSetValue === 'string' || typeof autoSetValue === 'number') {
				return value === autoSetValue;
			}

			// For object values (e.g., {width, height})
			if (typeof autoSetValue === 'object' && typeof value === 'object') {
				const autoSetWidth = this._get(autoSetValue, 'width');
				const autoSetHeight = this._get(autoSetValue, 'height');
				const valueWidth = this._get(value, 'width');
				const valueHeight = this._get(value, 'height');

				if (autoSetWidth && autoSetHeight && valueWidth && valueHeight) {
					return autoSetWidth === valueWidth && autoSetHeight === valueHeight;
				}
			}

			// Also check if autoSetValue is an object but value is a string like "1920x1080"
			// This can happen if format is 'value' but autoSetValue was provided as {width, height}
			if (typeof autoSetValue === 'object' && typeof value === 'string') {
				const autoSetWidth = this._get(autoSetValue, 'width');
				const autoSetHeight = this._get(autoSetValue, 'height');
				if (autoSetWidth && autoSetHeight) {
					const expectedString = `${autoSetWidth}x${autoSetHeight}`;
					return value === expectedString;
				}
			}

			// Also check if value is an object but autoSetValue is a string like "1920x1080"
			// This can happen if format is not 'value' but autoSetValue was provided as string
			if (typeof value === 'object' && typeof autoSetValue === 'string') {
				const valueWidth = this._get(value, 'width');
				const valueHeight = this._get(value, 'height');
				if (valueWidth && valueHeight) {
					const expectedString = `${valueWidth}x${valueHeight}`;
					return autoSetValue === expectedString;
				}
			}

			return false;
		},
	},
	computed: {
		...mapState([
			'user',
		]),
		logicOnFields () {
			const logic = getLogicOnFieldsFromField(this.field, this.inputValues);
			return applyAspectRatioDisabledMessage(logic, this.field, this.inputValues);
		},
		autoSetValue () {
			return this.logicOnFields.autoSetValue;
		},
		isShown () {
			const hasField = !!(this.field && (this.field.__rfield || this.field.__rgroup));
			const showsCondition = !this.logicOnFields.allShows.length || this.logicOnFields.triggeredShows.length;
			const adminCondition = !this.field.adminOnly || (this.user && this.user.isAdmin);

			// For autoSet fields, always show the component so autoSet values can be processed
			// The visibility will be controlled by CSS display: none when autoSetVisible: false
			const hasAutoSetLogic = this.field.logic && this.field.logic.field &&
				Object.values(this.field.logic.field).some((logic: any) => logic.autoSet);

			if (hasAutoSetLogic) {
				// Always show autoSet fields so they can process autoSet values
				return !!(hasField && showsCondition && adminCondition);
			}

			// For non-autoSet fields, use the original logic
			return !!(hasField && showsCondition && adminCondition);
		},
		isAutoSetFieldHidden () {
			// Check if this is an autoSet field that should be hidden
			const hasAutoSetLogic = this.field.logic && this.field.logic.field &&
				Object.values(this.field.logic.field).some((logic: any) => logic.autoSet);

			return hasAutoSetLogic && this.logicOnFields.autoSetVisible === false;
		},
		isDisabled () {
			const hasRegularDisables = !!(
				this.isShown &&
				this.logicOnFields.triggeredDisables.length
			);

			// Check if autoSet field is visible but not editable
			const hasAutoSetDisable = !!(
				this.logicOnFields.autoSetValue !== undefined &&
				this.logicOnFields.autoSetVisible &&
				!this.logicOnFields.autoSetAllowEdit
			);

			return hasRegularDisables || hasAutoSetDisable;
		},
		hasAutoSetError () {
			return !!this.logicOnFields.autoSetError;
		},
		autoSetError () {
			return this.logicOnFields.autoSetError;
		},
	},
	watch: {
		autoSetError: {
			handler (newVal: string, oldVal: string) {
				if (newVal && newVal !== oldVal) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: newVal,
						show: true,
					});
				}
			},
			immediate: true,
		},
	},
	components: {
		WidthHeightField,
		SelectField,
		SliderField,
	},
});
